import React, { memo, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { validators, formats } from 'investira.sdk';
import { ATIVO_TIPO } from '@investirapri/financa.js/lib/enums';
import {
  Typography,
  Stack,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TabContext,
  Box,
  TabList,
  Tab,
  TableContainer,
  Badge,
  IconButton,
  Tooltip,
  Icon
} from 'investira.react.components';
import CorrelacaoAtivoTooltip from './CorrelacaoAtivoTooltip';
import { PessoaImage } from '../../../../components/molecules';
import { PATHS } from '../../../../enums';
import helpers from '../../../../utils/helpers';

const DetalhesAtivos = memo(props => {
  const { ativos, variacoes, correlacoes, benchmarks } = props;

  const [tab, setTab] = useState(null);
  const [groupByAtivoTipo, setGroupByAtivoTipo] = useState([]);

  const commonColumns = {
    descricao: { key: 'descricao', label: '', align: 'left' },
    pessoa: { key: 'pessoa', label: '', align: 'left' },
    ativo: { key: 'ativo', label: 'Ativo', align: 'left' },
    ticker: { key: 'ticker', label: 'Ticker', align: 'left' },
    isin: { key: 'isin', label: 'ISIN', align: 'left' },
    emissor: { key: 'emissor', label: 'Emissor', align: 'left' },
    financeiro: { key: 'financeiro', label: 'Financeiro', align: 'right' },
    vencimento: { key: 'vencimento', label: 'Vencimento', align: 'left' },
    codigo: { key: 'codigo', label: 'Código', align: 'left' },
    ativo_tipo: { key: 'ativo_tipo', label: 'Tipo de Ativo', align: 'left' },
    serie: { key: 'serie', label: 'Série', align: 'left' },
    curva: { key: 'curva_id', label: 'Curva', align: 'left' },
    benchmark_id: { key: 'indicador_id', label: 'Correlação', align: 'left' },
    beta: { key: 'beta', label: 'Beta', align: 'left' },
    fator_ano: {
      key: 'fator_ano',
      label: 'Fator Ano',
      align: 'right',
      sx: { whiteSpace: 'nowrap' }
    },
    impacto_percentual: {
      key: 'impacto_percentual',
      label: 'Impacto (%)',
      align: 'right',
      sx: { whiteSpace: 'nowrap' }
    },
    impacto_financeiro: {
      key: 'impacto_financeiro',
      label: 'Impacto (R$)',
      align: 'right',
      sx: { whiteSpace: 'nowrap' }
    }
  };

  const columns = {
    FND: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.isin,
      commonColumns.codigo,
      commonColumns.benchmark_id,
      commonColumns.beta,
      commonColumns.impacto_percentual,
      commonColumns.impacto_financeiro,
      commonColumns.financeiro
    ],
    TPU: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.vencimento,
      commonColumns.isin,
      commonColumns.curva,
      commonColumns.serie,
      commonColumns.codigo,
      commonColumns.benchmark_id,
      commonColumns.beta,
      commonColumns.impacto_percentual,
      commonColumns.impacto_financeiro,
      commonColumns.financeiro
    ],
    TPR: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.ticker,
      commonColumns.isin,
      commonColumns.emissor,
      commonColumns.curva,
      commonColumns.vencimento,
      commonColumns.codigo,
      commonColumns.benchmark_id,
      commonColumns.beta,
      commonColumns.impacto_percentual,
      commonColumns.impacto_financeiro,
      commonColumns.financeiro
    ],
    TBA: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.ticker,
      commonColumns.isin,
      commonColumns.emissor,
      commonColumns.curva,
      commonColumns.vencimento,
      commonColumns.codigo,
      commonColumns.benchmark_id,
      commonColumns.beta,
      commonColumns.impacto_percentual,
      commonColumns.impacto_financeiro,
      commonColumns.financeiro
    ],
    ACN: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.ticker,
      commonColumns.isin,
      commonColumns.benchmark_id,
      commonColumns.beta,
      commonColumns.impacto_percentual,
      commonColumns.impacto_financeiro,
      commonColumns.financeiro
    ],
    DRI: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.vencimento,
      commonColumns.ticker,
      commonColumns.isin,
      commonColumns.codigo,
      commonColumns.benchmark_id,
      commonColumns.beta,
      commonColumns.impacto_percentual,
      commonColumns.impacto_financeiro,
      commonColumns.financeiro
    ],
    CRP: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.ticker,
      commonColumns.benchmark_id,
      commonColumns.beta,
      commonColumns.impacto_percentual,
      commonColumns.impacto_financeiro,
      commonColumns.financeiro
    ],
    IMV: [
      commonColumns.descricao,
      commonColumns.benchmark_id,
      commonColumns.beta,
      commonColumns.impacto_percentual,
      commonColumns.impacto_financeiro,
      commonColumns.financeiro
    ],
    BEM: [
      commonColumns.descricao,
      commonColumns.benchmark_id,
      commonColumns.beta,
      commonColumns.impacto_percentual,
      commonColumns.impacto_financeiro,
      commonColumns.financeiro
    ]
  };

  const handleChange = (_event, newValue) => {
    setTab(newValue);
  };

  const agruparPorAtivoTipoId = useCallback(pRegra => {
    // Verifica se o input é um array
    if (!Array.isArray(pRegra)) {
      console.error('O parâmetro deve ser um array.');
      return {};
    }

    const agrupado = {};

    pRegra.forEach(item => {
      const tipoId = item.ativo_tipo_id;

      // Se o tipo não existir no objeto, cria um array vazio para ele
      if (!agrupado[tipoId]) {
        agrupado[tipoId] = [];
      }

      // Adiciona o item ao grupo correspondente
      agrupado[tipoId].push(item);
    });

    return agrupado;
  }, []);

  useEffect(() => {
    if (!validators.isNull(ativos)) {
      const xGroupByAtivoTipo = agruparPorAtivoTipoId(ativos);
      const xFirstKey = Object.keys(xGroupByAtivoTipo)[0];
      setTab(xFirstKey);
      setGroupByAtivoTipo(xGroupByAtivoTipo);
    }
  }, [ativos, agruparPorAtivoTipoId]);

  return (
    <>
      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Ativo Tipo">
            {Object.entries(groupByAtivoTipo).map(([xKey, xValue]) => {
              return <Tab key={`tab-${xKey}`} label={ATIVO_TIPO[xKey]?.sigla} value={xKey} />;
            })}
          </TabList>
        </Box>
      </TabContext>

      <Stack pb={6}>
        <Stack py={1} px={2}>
          <Typography variant="caption" color="textSecondary">
            {ATIVO_TIPO[tab]?.descricao}
          </Typography>
        </Stack>
        <Divider />
        <TableContainer
          id="table-container"
          sx={{
            overflow: 'auto'
          }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {tab &&
                  columns[ATIVO_TIPO[tab]?.mercado]?.map(column => (
                    <TableCell key={column.key} align={column.align} sx={column.sx || {}}>
                      {column.label}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={
                    !validators.isNull(tab) ? columns[ATIVO_TIPO[tab]?.mercado].length - 1 : 0
                  }></TableCell>
                <TableCell align="right">
                  <b>
                    {!validators.isEmpty(groupByAtivoTipo) &&
                      formats.formatNumber(
                        groupByAtivoTipo[tab]?.reduce((acc, curr) => acc + curr.financeiro, 0) || 0,
                        2,
                        true
                      )}
                  </b>
                </TableCell>
              </TableRow>
              {!validators.isEmpty(groupByAtivoTipo) &&
                groupByAtivoTipo[tab].map(xAtivo => {
                  const xMercado = ATIVO_TIPO[tab]?.mercado;
                  xAtivo.mercado = xMercado;
                  const xPath = xMercado
                    ? `/ativos${PATHS[xMercado.toLowerCase()].path}/${xAtivo.ifeed_ativo_id}`
                    : null;

                  return (
                    <TableRow key={xAtivo.ativo_key}>
                      {tab &&
                        columns[ATIVO_TIPO[tab]?.mercado]?.map(column => {
                          if (column.key === 'pessoa') {
                            return (
                              <TableCell key={column.key} sx={{ width: '24px' }}>
                                <Link
                                  to={`/empresas/${xAtivo.emissor_pessoa_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer">
                                  <PessoaImage
                                    logoPessoaId={xAtivo?.emissor.logo_pessoa_id}
                                    alt={xAtivo?.emissor?.pessoa_fantasia}
                                    hasLogo={Boolean(xAtivo?.emissor?.logo_pessoa_id)}
                                    size={24}
                                  />
                                </Link>
                              </TableCell>
                            );
                          }

                          const value = xAtivo[column.key];
                          const xAtivoDescricao = helpers.getAtivoDescricao(xAtivo);

                          if (column.key === 'ativo') {
                            return (
                              <TableCell
                                key={column.key}
                                sx={{
                                  width: '24px',
                                  whiteSpace: 'nowrap'
                                }}>
                                <Badge
                                  badgeContent={xAtivo.marcacao}
                                  color="warning"
                                  anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                  }}>
                                  <Box pr={1.5}>
                                    {xPath ? (
                                      <Typography
                                        component={Link}
                                        to={xPath}
                                        color="primary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        aria-label={`Abrir ${xAtivoDescricao} em uma nova aba`}>
                                        {xAtivoDescricao}
                                      </Typography>
                                    ) : (
                                      xAtivoDescricao
                                    )}
                                  </Box>
                                </Badge>
                              </TableCell>
                            );
                          }

                          if (column.key === 'emissor') {
                            return (
                              <TableCell
                                key={column.key}
                                sx={{
                                  width: '24px',
                                  whiteSpace: 'nowrap'
                                }}>
                                {value?.pessoa_fantasia}
                              </TableCell>
                            );
                          }

                          if (column.key === 'indicador_id') {
                            return (
                              <TableCell
                                key={column.key}
                                sx={{
                                  width: '24px',
                                  whiteSpace: 'nowrap',
                                  verticalAlign: 'middle'
                                }}>
                                <Typography
                                  component={Link}
                                  to={`/indicadores/${value}`}
                                  color="primary"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="body2"
                                  aria-label={`Abrir ${value} em uma nova aba`}>
                                  {value}
                                </Typography>
                                {xAtivo.correlacao.correlacao !== 0 && (
                                  <Tooltip
                                    title={
                                      <CorrelacaoAtivoTooltip
                                        ativo={xAtivo}
                                        correlacoes={correlacoes}
                                        benchmarks={benchmarks}
                                      />
                                    }
                                    arrow={true}>
                                    <IconButton color="primary" size="small">
                                      <Icon iconName="information" size={16} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            );
                          }

                          if (column.key === 'fator_ano') {
                            const xIndicadorId = xAtivo.indicador_id;
                            return (
                              <TableCell
                                key={column.key}
                                sx={{
                                  width: '24px',
                                  whiteSpace: 'nowrap',
                                  verticalAlign: 'middle',
                                  textAlign: 'right'
                                }}>
                                {variacoes[xIndicadorId]
                                  ? formats.formatNumber(variacoes[xIndicadorId].fator_ano, 5)
                                  : '---'}
                              </TableCell>
                            );
                          }

                          const isDate = ['operacao_data', 'emissao', 'vencimento'].includes(
                            column.key
                          );

                          const isNumber = [
                            'quantidade',
                            'pu',
                            'financeiro',
                            'impacto_percentual',
                            'impacto_financeiro',
                            'taxa'
                          ].includes(column.key);

                          return (
                            <TableCell key={column.key} align={column.align}>
                              {isNumber && value
                                ? formats.formatNumber(value, 2, true)
                                : isDate && value
                                ? formats.formatDateCustom(value, 'DD/MM/YYYY')
                                : value}

                              {}
                            </TableCell>
                          );
                        })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </>
  );
});

export default DetalhesAtivos;
